import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { AuthLayout } from "@/components/Layout";
import { AuthCard } from "@/components/Card";
import { Button } from "@/components/Button";
import { FormField } from "@/components/FormField";
import { Alert } from "@/components/Alert";
import { Logo } from "@/components/Logo";
import { LoadingScreen } from "@/components/LoadingScreen";

export default function AdminMfaChallenge() {
  const nav = useNavigate();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  // Look up the patient's verified TOTP factor and issue a challenge.
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const { data: factors, error: listErr } = await supabase.auth.mfa.listFactors();
        if (listErr) throw listErr;
        const verified = factors?.totp?.find((f) => f.status === "verified");
        if (!verified) {
          // No factor enrolled — guard should have routed to /admin/mfa/setup.
          nav("/admin/mfa/setup", { replace: true });
          return;
        }
        if (cancelled) return;
        setFactorId(verified.id);
        const challenge = await supabase.auth.mfa.challenge({ factorId: verified.id });
        if (challenge.error) throw challenge.error;
        if (cancelled) return;
        setChallengeId(challenge.data.id);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, [nav]);

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!factorId || !challengeId || code.length !== 6) return;
    setVerifying(true);
    setError(null);
    try {
      const verify = await supabase.auth.mfa.verify({ factorId, challengeId, code });
      if (verify.error) throw verify.error;
      nav("/admin/users", { replace: true });
    } catch (e) {
      setError((e as Error).message ?? "That code didn't match. Try again.");
      // Issue a fresh challenge so the user can retry.
      const next = await supabase.auth.mfa.challenge({ factorId });
      if (next.data) setChallengeId(next.data.id);
    } finally {
      setVerifying(false);
    }
  }

  if (bootstrapping) return <LoadingScreen label="Issuing a verification challenge…" />;

  return (
    <AuthLayout>
      <AuthCard>
        <div className="mb-6 flex justify-center"><Logo size="lg" /></div>
        <div className="flex justify-center mb-1.5">
          <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-fh-gray bg-fh-sand rounded-full px-2.5 py-0.5">
            Admin · verification
          </span>
        </div>
        <h1 className="font-serif text-[25px] font-normal text-fh-ink text-center mb-1.5">
          Two-factor code
        </h1>
        <p className="text-[14px] text-fh-gray text-center mb-5">
          Enter the 6-digit code from your authenticator app.
        </p>

        <form onSubmit={onVerify} className="flex flex-col gap-3.5">
          {error && <Alert tone="error">{error}</Alert>}
          <FormField
            label="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <Button
            type="submit"
            loading={verifying}
            disabled={code.length !== 6}
            fullWidth
          >
            Verify
          </Button>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}
